import { c as _c } from "react/compiler-runtime";
import { getOrgCredentialsQueryKey, postOrgCredentialsMutation } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import type { CreateAgentCredentialRequest } from '@onefootprint/request-types/dashboard';
import { Dialog, Form, SegmentedControl, useToast } from '@onefootprint/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { CredentialsScope } from '../../types';
import AgentPicker from './agent-picker';
type CredentialKind = 'one_password' | 'static_credential';
type FormValues = {
  label: string;
  site: string;
  username: string;
  password: string;
  serviceAccountToken: string;
  agentIds: string[];
};
type AddCredentialDialogProps = {
  scope: CredentialsScope;
  onClose: () => void;
};
const AddCredentialDialog = t0 => {
  const $ = _c(83);
  const {
    scope,
    onClose
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "credentials"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const queryClient = useQueryClient();
  const {
    toast
  } = useToast();
  const showRequestErrorToast = useRequestErrorToast();
  const [kind, setKind] = useState("static_credential");
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      defaultValues: DEFAULT_VALUES,
      shouldUnregister: true
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const formMethods = useForm(t2);
  const {
    handleSubmit
  } = formMethods;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = postOrgCredentialsMutation();
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== onClose || $[4] !== queryClient || $[5] !== scope.kind || $[6] !== t || $[7] !== toast) {
    t4 = async credential => {
      await queryClient.invalidateQueries({
        queryKey: getOrgCredentialsQueryKey()
      });
      const title = scope.kind === "agent" ? t(_temp, {
        name: credential.label
      }) : t(_temp2, {
        name: credential.label
      });
      toast({
        variant: "default",
        title,
        description: ""
      });
      onClose();
    };
    $[3] = onClose;
    $[4] = queryClient;
    $[5] = scope.kind;
    $[6] = t;
    $[7] = toast;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== showRequestErrorToast || $[10] !== t4) {
    t5 = {
      ...t3,
      onSuccess: t4,
      onError: showRequestErrorToast
    };
    $[9] = showRequestErrorToast;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const mutation = useMutation(t5);
  let t6;
  if ($[12] !== mutation.isPending || $[13] !== onClose) {
    t6 = () => {
      if (!mutation.isPending) {
        onClose();
      }
    };
    $[12] = mutation.isPending;
    $[13] = onClose;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleClose = t6;
  let t7;
  if ($[15] !== kind || $[16] !== mutation || $[17] !== scope.agentId || $[18] !== scope.kind) {
    t7 = values => {
      const agentIds = scope.kind === "agent" ? [scope.agentId] : values.agentIds;
      mutation.mutate({
        body: toRequest(kind, values, agentIds)
      });
    };
    $[15] = kind;
    $[16] = mutation;
    $[17] = scope.agentId;
    $[18] = scope.kind;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const onSubmit = t7;
  let t8;
  if ($[20] !== t) {
    t8 = t(_temp3);
    $[20] = t;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t) {
    t9 = t(_temp4);
    $[22] = t;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== mutation.isPending || $[25] !== t9) {
    t10 = {
      form: "add-credential-form",
      label: t9,
      loading: mutation.isPending,
      disabled: mutation.isPending,
      type: "submit"
    };
    $[24] = mutation.isPending;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t) {
    t11 = t(_temp5);
    $[27] = t;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== handleClose || $[30] !== mutation.isPending || $[31] !== t11) {
    t12 = {
      label: t11,
      disabled: mutation.isPending,
      onClick: handleClose
    };
    $[29] = handleClose;
    $[30] = mutation.isPending;
    $[31] = t11;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== handleSubmit || $[34] !== onSubmit) {
    t13 = handleSubmit(onSubmit);
    $[33] = handleSubmit;
    $[34] = onSubmit;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t) {
    t14 = t(_temp6);
    $[36] = t;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== t) {
    t15 = t(_temp7);
    $[38] = t;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== t15) {
    t16 = {
      value: "static_credential",
      label: t15
    };
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== t) {
    t17 = t(_temp8);
    $[42] = t;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== t17) {
    t18 = {
      value: "one_password",
      label: t17
    };
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== t16 || $[47] !== t18) {
    t19 = [t16, t18];
    $[46] = t16;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== kind || $[50] !== t14 || $[51] !== t19) {
    t20 = <div className="flex justify-center"><SegmentedControl aria-label={t14} size="compact" value={kind} onChange={setKind} options={t19} /></div>;
    $[49] = kind;
    $[50] = t14;
    $[51] = t19;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== t) {
    t21 = t(_temp9);
    $[53] = t;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== kind || $[56] !== t) {
    t22 = kind === "one_password" ? t(_temp10) : t(_temp11);
    $[55] = kind;
    $[56] = t;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== t) {
    t23 = {
      validate: value => String(value).trim().length > 0 || t(_temp12)
    };
    $[58] = t;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] !== t21 || $[61] !== t22 || $[62] !== t23) {
    t24 = <Form.Input name="label" size="compact" label={t21} placeholder={t22} rules={t23} />;
    $[60] = t21;
    $[61] = t22;
    $[62] = t23;
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  let t25;
  if ($[64] !== kind) {
    t25 = kind === "one_password" ? <OnePasswordFields /> : <LoginFields />;
    $[64] = kind;
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  let t26;
  if ($[66] !== scope.kind) {
    t26 = scope.kind === "user" && <AgentPicker />;
    $[66] = scope.kind;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  let t27;
  if ($[68] !== t13 || $[69] !== t20 || $[70] !== t24 || $[71] !== t25 || $[72] !== t26) {
    t27 = <form id="add-credential-form" onSubmit={t13} className="flex flex-col gap-4">{t20}{t24}{t25}{t26}</form>;
    $[68] = t13;
    $[69] = t20;
    $[70] = t24;
    $[71] = t25;
    $[72] = t26;
    $[73] = t27;
  } else {
    t27 = $[73];
  }
  let t28;
  if ($[74] !== formMethods || $[75] !== t27) {
    t28 = <FormProvider {...formMethods}>{t27}</FormProvider>;
    $[74] = formMethods;
    $[75] = t27;
    $[76] = t28;
  } else {
    t28 = $[76];
  }
  let t29;
  if ($[77] !== handleClose || $[78] !== t10 || $[79] !== t12 || $[80] !== t28 || $[81] !== t8) {
    t29 = <Dialog open={true} onClose={handleClose} title={t8} size="compact" primaryButton={t10} secondaryButton={t12}>{t28}</Dialog>;
    $[77] = handleClose;
    $[78] = t10;
    $[79] = t12;
    $[80] = t28;
    $[81] = t8;
    $[82] = t29;
  } else {
    t29 = $[82];
  }
  return t29;
};
const OnePasswordFields = () => {
  const $ = _c(14);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "credentials.dialog.fields"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t0);
  let t1;
  if ($[1] !== t) {
    t1 = t(_temp13);
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t) {
    t2 = t(_temp14);
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = t(_temp15);
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t) {
    t4 = {
      validate: value => {
        const token = String(value).trim();
        if (!token) {
          return t(_temp16);
        }
        return token.startsWith(TOKEN_PREFIX) || t(_temp17);
      }
    };
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = <Form.Input name="serviceAccountToken" size="compact" type="password" autoComplete="off" label={t1} placeholder={t2} hint={t3} rules={t4} />;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};
const LoginFields = () => {
  const $ = _c(37);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "credentials.dialog.fields"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t0);
  let t1;
  if ($[1] !== t) {
    t1 = t(_temp18);
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t) {
    t2 = t(_temp19);
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = {
      validate: value => {
        const site = String(value).trim();
        if (!site) {
          return t(_temp20);
        }
        return originOf(site) !== null || t(_temp21);
      }
    };
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = <Form.Input name="site" size="compact" inputMode="url" autoCapitalize="none" autoCorrect="off" label={t1} placeholder={t2} rules={t3} />;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t) {
    t5 = t(_temp22);
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t) {
    t6 = t(_temp23);
    $[13] = t;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t) {
    t7 = {
      validate: value_0 => String(value_0).trim().length > 0 || t(_temp24)
    };
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t5 || $[18] !== t6 || $[19] !== t7) {
    t8 = <Form.Input name="username" size="compact" autoComplete="off" autoCapitalize="none" autoCorrect="off" label={t5} placeholder={t6} rules={t7} />;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t) {
    t9 = t(_temp25);
    $[21] = t;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t) {
    t10 = t(_temp26);
    $[23] = t;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t) {
    t11 = t(_temp27);
    $[25] = t;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t11) {
    t12 = {
      required: t11
    };
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t10 || $[30] !== t12 || $[31] !== t9) {
    t13 = <Form.Input name="password" size="compact" type="password" autoComplete="new-password" label={t9} placeholder={t10} rules={t12} />;
    $[29] = t10;
    $[30] = t12;
    $[31] = t9;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t13 || $[34] !== t4 || $[35] !== t8) {
    t14 = <>{t4}{t8}{t13}</>;
    $[33] = t13;
    $[34] = t4;
    $[35] = t8;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  return t14;
};
const TOKEN_PREFIX = 'ops_';
const DEFAULT_VALUES: FormValues = {
  label: '',
  site: '',
  username: '',
  password: '',
  serviceAccountToken: '',
  agentIds: []
};
const toRequest = (kind: CredentialKind, values: FormValues, agentIds: string[]): CreateAgentCredentialRequest => {
  const shared = {
    label: values.label.trim(),
    agentIds,
    temporary: false
  };
  if (kind === 'one_password') {
    return {
      ...shared,
      provider: {
        kind: 'one_password',
        serviceAccountToken: values.serviceAccountToken.trim(),
        items: []
      }
    };
  }
  const site = values.site.trim();
  return {
    ...shared,
    provider: {
      kind: 'static_credential',
      username: values.username.trim(),
      password: values.password,
      allowedOrigins: [originOf(site) ?? site]
    }
  };
};
const originOf = (url: string): string | null => {
  try {
    const {
      protocol,
      origin
    } = new URL(url);
    return protocol === 'https:' || protocol === 'http:' ? origin : null;
  } catch {
    return null;
  }
};
export default AddCredentialDialog;
function _temp($) {
  return $.dialog.successAgent;
}
function _temp2($_0) {
  return $_0.dialog.success;
}
function _temp3($_1) {
  return $_1.dialog.title;
}
function _temp4($_2) {
  return $_2.dialog.submit;
}
function _temp5($_3) {
  return $_3.dialog.cancel;
}
function _temp6($_4) {
  return $_4.dialog.kindAriaLabel;
}
function _temp7($_5) {
  return $_5.kind.password;
}
function _temp8($_6) {
  return $_6.kind.onePassword;
}
function _temp9($_7) {
  return $_7.dialog.fields.name;
}
function _temp10($_8) {
  return $_8.dialog.fields.namePlaceholderOnePassword;
}
function _temp11($_9) {
  return $_9.dialog.fields.namePlaceholderPassword;
}
function _temp12($_10) {
  return $_10.dialog.fields.nameRequired;
}
function _temp13($) {
  return $.token;
}
function _temp14($_0) {
  return $_0.tokenPlaceholder;
}
function _temp15($_1) {
  return $_1.tokenHint;
}
function _temp16($_2) {
  return $_2.tokenRequired;
}
function _temp17($_3) {
  return $_3.tokenInvalid;
}
function _temp18($) {
  return $.site;
}
function _temp19($_0) {
  return $_0.sitePlaceholder;
}
function _temp20($_1) {
  return $_1.siteRequired;
}
function _temp21($_2) {
  return $_2.siteInvalid;
}
function _temp22($_3) {
  return $_3.username;
}
function _temp23($_4) {
  return $_4.usernamePlaceholder;
}
function _temp24($_5) {
  return $_5.usernameRequired;
}
function _temp25($_6) {
  return $_6.password;
}
function _temp26($_7) {
  return $_7.passwordPlaceholder;
}
function _temp27($_8) {
  return $_8.passwordRequired;
}